<script setup lang="ts">
import type { MediaElementProps } from "./MediaElementProps";
import { MediaType, type PageBlock } from "@/views/blocks/PageBlock.interface";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import Media from "@/views/blocks/shared/section/media/media/Media.vue";
import ScrollAnimation from "@/views/blocks/shared/section/animations/ScrollAnimation.vue";
import { computed } from "vue";
import HorizontalSpacing from "@/views/blocks/shared/section/layout/spacing/HorizontalSpacing.vue";

const props = defineProps<{
    block: PageBlock<MediaElementProps>;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

// Calculate dimensions based on maxWidth class
const mediaDimensions = computed(() => {
    const maxWidthClass = dataValues.value.formatting.data.maxWidth;

    let width: number;
    let height: number;

    switch (maxWidthClass) {
        case "max-w-2xl":
            width = 672;
            height = 378;
            break;
        case "max-w-3xl":
            width = 768;
            height = 432;
            break;
        case "max-w-4xl":
            width = 896;
            height = 504;
            break;
        case "max-w-5xl":
            width = 1024;
            height = 576;
            break;
        case "max-w-6xl":
            width = 1152;
            height = 648;
            break;
        case "max-w-7xl":
            width = 1280;
            height = 720;
            break;
        default:
            width = 1280;
            height = 720;
    }

    return { width, height };
});
</script>

<template>
    <ScrollAnimation :type="scrollAnimationType">
        <div
            :id="block.webId"
            :style="{
                backgroundColor: styleValues.background,
            }"
        >
            <HorizontalSpacing
                :class="[
                    dataValues.formatting.data.paddingTop,
                    dataValues.formatting.data.paddingBottom,
                ]"
            >
                <div class="mx-auto max-w-2xl lg:max-w-7xl">
                    <EditableElement
                        :blockId="block.id"
                        field-name="media"
                        v-if="!dataValues.media.isHidden"
                    >
                        <div
                            :class="[
                                'rounded-lg overflow-hidden',
                                dataValues.media.data.mediaType === MediaType.IFRAME &&
                                !dataValues.formatting.data.iframeHeight
                                    ? 'aspect-[16/9]'
                                    : '',
                                dataValues.formatting.data.elementAlign,
                                dataValues.formatting.data.maxWidth,
                            ]"
                        >
                            <Media
                                :data="{
                                    src: dataValues.media.data.src,
                                    alt: dataValues.media.data.alt,
                                    mediaType: dataValues.media.data.mediaType,
                                    videoSrc: dataValues.media.data.videoSrc,
                                    videoAutoplay: dataValues.media.data.videoAutoplay,
                                    videoControls: dataValues.media.data.videoControls,
                                    videoLoop: dataValues.media.data.videoLoop,
                                    iframeSrc: dataValues.media.data.iframeSrc,
                                }"
                                :image-width="mediaDimensions.width"
                                :image-height="mediaDimensions.height"
                                :video-width="mediaDimensions.width"
                                :video-height="mediaDimensions.height"
                                :iframe-height="dataValues.formatting.data.iframeHeight"
                            />
                        </div>
                    </EditableElement>
                </div>
            </HorizontalSpacing>
        </div>
    </ScrollAnimation>
</template>
